import { z } from 'zod'

export const TX_PURCHASE_ORDER = 'PurchaseOrder'
export const TX_WORK_ORDER_CONSUMPTION = 'WorkOrderConsumption'
export const TX_WORK_ORDER_PRODUCTION = 'WorkOrderProduction'
export const TX_SALES_ORDER = 'SalesOrder'
export const TX_INITIAL = 'Initial'

export const ForecastTransactionSchema = z.object({
  id: z.string(),
  itemId: z.number(),
  week: z.number(),
  qty: z.number(),
  transactionType: z.union([
    z.literal(TX_INITIAL),
    z.literal(TX_PURCHASE_ORDER),
    z.literal(TX_WORK_ORDER_CONSUMPTION),
    z.literal(TX_WORK_ORDER_PRODUCTION),
    z.literal(TX_SALES_ORDER),
  ]),
  documentSourceType: z.string(),
  documentSourceId: z.string(),
  documentSourceNumber: z.string(),
  calculatedDate: z.coerce.date(),
  status: z.union([
    z.literal('Planned'),
    z.literal('Confirmed'),
    z.literal('AtRisk'),
  ]),
  notes: z.string().nullish(),
})

export type ForecastTransactionModel = z.infer<typeof ForecastTransactionSchema>

export const WeeklyProjectionSchema = z.object({
  itemId: z.number().int(),
  week: z.number(),
  openingBalance: z.number(),
  closingBalance: z.number(),
  transactions: z.array(ForecastTransactionSchema),
})

export type WeeklyProjectionModel = z.infer<typeof WeeklyProjectionSchema>

export const MaterialForecastSchema = z.object({
  itemId: z.number().int(),
  code: z.string(),
  name: z.string(),
  materialType: z.union([z.literal('RawMaterial'), z.literal('FinishedGoods')]),
  isTradeGood: z.boolean().optional().default(false),
  weeklyProjections: z.array(WeeklyProjectionSchema),
})

export const FGTypeForecastSchema = z.object({
  type: z.string(),
  materialType: z.literal('FinishedGoods'),
  isTradeGood: z.boolean().optional().default(false),
  weeklyProjections: z.array(WeeklyProjectionSchema),
})

export type FGTypeForecastModel = z.infer<typeof FGTypeForecastSchema>

export const Sale1230GetResultSchema = z.object({
  id: z.string(),
  number: z.string(),
  date: z.coerce.date(),
  status: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  projectionWeeks: z.number(),
  generatedDate: z.coerce.date(),
  materialForecasts: z.array(MaterialForecastSchema),
})

export type MaterialForecastModel = z.infer<typeof MaterialForecastSchema>

export type Sale1230GetResultModel = z.infer<typeof Sale1230GetResultSchema>

export const MaterialForecastRowSchema = z
  .object({
    metric: z.union([
      z.literal('Opening'),
      z.literal('Incoming'),
      z.literal('Outgoing'),
      z.literal('Closing'),
    ]),
  })
  .merge(MaterialForecastSchema)

export type MaterialForecastRowModel = z.infer<typeof MaterialForecastRowSchema>

export const FgTypeForecastRowSchema = z
  .object({
    metric: z.union([
      z.literal('Opening'),
      z.literal('Incoming'),
      z.literal('Outgoing'),
      z.literal('Closing'),
    ]),
  })
  .merge(FGTypeForecastSchema)

export type FgTypeForecastRowModel = z.infer<typeof FgTypeForecastRowSchema>

// Helper functions
export const getStatusColor = (weeklyProjection?: WeeklyProjectionModel) => {
  if (!weeklyProjection) return '#ffffff'

  let status = 'Unknown'

  if (
    weeklyProjection.openingBalance > 0 &&
    weeklyProjection.closingBalance > 0 &&
    weeklyProjection.closingBalance > weeklyProjection.openingBalance
  ) {
    status = 'Healthy'
  } else if (
    weeklyProjection.openingBalance > 0 &&
    weeklyProjection.closingBalance > 0 &&
    weeklyProjection.closingBalance < weeklyProjection.openingBalance
  ) {
    status = 'Declining'
  } else if (
    weeklyProjection.openingBalance <= 0 &&
    weeklyProjection.closingBalance > 0
  ) {
    status = 'Excess'
  } else if (
    weeklyProjection.openingBalance < 0 &&
    weeklyProjection.closingBalance < 0
  ) {
    status = 'Shortage'
  } else if (
    weeklyProjection.openingBalance > 0 &&
    weeklyProjection.closingBalance < 0
  ) {
    status = 'Shortage'
  } else if (
    weeklyProjection.openingBalance === 0 &&
    weeklyProjection.closingBalance < 0
  ) {
    status = 'Shortage'
  } else if (
    weeklyProjection.openingBalance < 0 &&
    weeklyProjection.closingBalance === 0
  ) {
    status = 'Warning'
  } else if (
    weeklyProjection.openingBalance === 0 &&
    weeklyProjection.closingBalance === 0
  ) {
    status = 'Warning'
  }

  switch (status) {
    case 'Healthy':
      return '#52c41a26' // Green - stock increasing
    case 'Declining':
      return '#b7eb8f26' // Light green - stock decreasing but safe
    case 'Warning':
      return '#faad1426' // Yellow - at zero or recovering to zero
    case 'Shortage':
      return '#f5222d26' // Red - negative stock
    case 'Excess':
      return '#1890ff26' // Blue - recovering from shortage
    default:
      return '#d9d9d926' // Gray - unknown status
  }
}

export const Sale1230FilterItemSchema = z.object({
  scenarioId: z.string().uuid().nullish(),
  fgItemId: z.coerce.number().int().nullish(),
  bomId: z.coerce.number().int().nullish(),
  showOnlyWithOutgoing: z.coerce.boolean().nullish().default(true),
})
